import { FC, useState } from 'react';
import { Button, Dropdown, TextInput, Toggle } from '@carbon/react';
import { useAtom } from 'jotai';
import { useDebouncedChange } from 'components/debounced-input';
import wellStatusTypes from 'enums/well_status_types.json';
import { EMPTY_FILTERS, unitWellAssignmentFiltersAtom } from './atoms';
import { ExportCsvButton } from './export-button';
import {
  ASSIGNMENT_TYPE_LABELS,
  TIER_LABELS,
  type UnitWellAssignmentFilters,
} from './types';
import styles from './page.module.scss';

type TextFilterKey =
  'wellUWI' | 'developmentAreaName' | 'currentOperator' | 'percentBelow';

interface Option {
  id: string;
  label: string;
}

const withAll = (label: string, entries: [string, string][]): Option[] => [
  { id: '', label },
  ...entries.map(([id, text]) => ({ id, label: text })),
];

// Every stored status but LTD, which names an undrilled slot, not a well.
const STATUS_ITEMS = withAll(
  'All statuses',
  Object.entries(wellStatusTypes).filter(([id]) => id !== 'LTD')
);
const TYPE_ITEMS = withAll('All types', Object.entries(ASSIGNMENT_TYPE_LABELS));
const TIER_ITEMS = withAll('All tiers', Object.entries(TIER_LABELS));

const TextFilter: FC<{
  id: string;
  label: string;
  placeholder: string;
  value: string;
  type?: 'text' | 'number';
  onChange: (value: string) => void;
}> = ({ id, label, placeholder, value, type = 'text', onChange }) => {
  const [local, change] = useDebouncedChange(value, onChange, 400);
  return (
    <TextInput
      id={id}
      className={styles.filterField}
      labelText={label}
      placeholder={placeholder}
      type={type}
      {...(type === 'number' ? { min: 0, max: 100 } : {})}
      value={local}
      onChange={(event) => change(event.target.value)}
    />
  );
};

const DropdownFilter: FC<{
  id: string;
  label: string;
  items: Option[];
  value: string;
  onChange: (value: string) => void;
}> = ({ id, label, items, value, onChange }) => (
  <Dropdown
    id={id}
    className={styles.filterField}
    titleText={label}
    label={items[0].label}
    items={items}
    itemToString={(item: Option | null) => item?.label ?? ''}
    selectedItem={items.find((item) => item.id === value) ?? items[0]}
    onChange={({ selectedItem }: { selectedItem: Option | null }) =>
      onChange(selectedItem?.id ?? '')
    }
  />
);

export const FilterBar: FC = () => {
  const [filters, setFilters] = useAtom(unitWellAssignmentFiltersAtom);
  // Bumped by Reset to remount the text boxes. A box typed into and not yet
  // sent still shows its own text over an unchanged filter value, and its
  // pending send would land after the reset; remounting drops both.
  const [resetCount, setResetCount] = useState(0);

  const reset = () => {
    setFilters(EMPTY_FILTERS);
    setResetCount((count) => count + 1);
  };

  // Any filter change returns to page 1; sort and page size are kept.
  const patch = (changes: Partial<UnitWellAssignmentFilters>) =>
    setFilters((previous) => ({ ...previous, page: 1, ...changes }));

  const textFilter = (
    key: TextFilterKey,
    label: string,
    placeholder: string,
    type: 'text' | 'number' = 'text'
  ) => (
    <TextFilter
      key={`${key}-${resetCount}`}
      id={`unit-well-assignments-${key}`}
      label={label}
      placeholder={placeholder}
      type={type}
      value={filters[key]}
      onChange={(value) => patch({ [key]: value.trim() })}
    />
  );

  return (
    <div className={styles.filterBar}>
      {textFilter('percentBelow', 'Percent in unit below', 'e.g. 90', 'number')}
      {textFilter('developmentAreaName', 'Unit', 'e.g. U-0000312441')}
      {textFilter('currentOperator', 'Operator contains', 'e.g. Devon')}
      {textFilter('wellUWI', 'UWI', 'e.g. 3002554535')}
      <DropdownFilter
        id="unit-well-assignments-status"
        label="Well status"
        items={STATUS_ITEMS}
        value={filters.wellStatus}
        onChange={(wellStatus) => patch({ wellStatus })}
      />
      <DropdownFilter
        id="unit-well-assignments-type"
        label="Assignment type"
        items={TYPE_ITEMS}
        value={filters.assignmentType}
        onChange={(assignmentType) => patch({ assignmentType })}
      />
      <DropdownFilter
        id="unit-well-assignments-tier"
        label="Producing segment tier"
        items={TIER_ITEMS}
        value={filters.tier}
        onChange={(tier) => patch({ tier })}
      />
      {/* Every row of each such well; sorted by UWI so a well's rows sit
          together (best fit before explicit). */}
      <Toggle
        id="unit-well-assignments-multiple"
        className={styles.filterField}
        labelText="Only wells with multiple assignments"
        labelA="Off"
        labelB="On"
        toggled={filters.multipleAssignments}
        onToggle={(multipleAssignments: boolean) =>
          patch(
            multipleAssignments
              ? { multipleAssignments, sortBy: 'wellUWI', sortDir: 'asc' }
              : { multipleAssignments }
          )
        }
      />
      <div className={styles.filterActions}>
        <Button kind="ghost" size="md" onClick={reset}>
          Reset
        </Button>
        <ExportCsvButton />
      </div>
    </div>
  );
};
